import { createElement, useEffect, useState, type ComponentType } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"

import { LoadingPage } from "@/components/System"
import { DEFAULT_LOADING_MESSAGE } from "@/constants"
import { useSysConfig } from "@/hooks"
import { useGetHistoryApi } from "@/hooks/api"
import type { Workspace } from "@/types"

import HistoryPage from "./History/history"
import LegacyHistoryList from "./HistoryList"
import HistorySelectionState from "./HistorySelectionState"
import ThreadList from "./ThreadList"
import { getLegacyHistoryId } from "./ThreadList/threadListState"

export interface HistoryListProps {
  workspace?: Workspace
}

interface HistoryPreflightState {
  historyId: number
  status: "loading" | "success" | "error"
  sourceType?: string
}

const ThreadHistoryPageOwner = ({ workspace }: { workspace: Workspace }) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const legacyHistoryId = getLegacyHistoryId(searchParams)
  const [preflight, setPreflight] = useState<HistoryPreflightState>()
  const { fetchFn: getHistory } = useGetHistoryApi(legacyHistoryId ?? -1)

  useEffect(() => {
    if (legacyHistoryId === null) {
      setPreflight(undefined)
      return
    }

    let active = true
    setPreflight({ historyId: legacyHistoryId, status: "loading" })
    void getHistory({
      callbackFn: (response) => {
        if (!active) return
        if (!response?.data || response.data.id !== legacyHistoryId) {
          setPreflight({ historyId: legacyHistoryId, status: "error" })
          return
        }
        setPreflight({
          historyId: legacyHistoryId,
          status: "success",
          sourceType: response.data.source_type,
        })
      },
    }).catch(() => {
      if (active) {
        setPreflight({ historyId: legacyHistoryId, status: "error" })
      }
    })
    return () => {
      active = false
    }
  }, [getHistory, legacyHistoryId])

  if (legacyHistoryId === null) {
    return createElement(ThreadList, { workspace })
  }
  if (
    preflight?.historyId !== legacyHistoryId ||
    preflight.status === "loading"
  ) {
    return createElement(LoadingPage, { message: DEFAULT_LOADING_MESSAGE })
  }
  const clearLegacyHistorySelection = () => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current.toString())
        params.delete("hid")
        return params
      },
      { replace: true }
    )
  }
  if (preflight.status === "error") {
    return createElement(HistorySelectionState, {
      kind: "error",
      title: t("historyList.thread.historyResolveFailed"),
      description: t("historyList.thread.historyResolveFailedDescription"),
      actionLabel: t("historyList.detail.backToList"),
      onAction: clearLegacyHistorySelection,
    })
  }
  if (
    preflight.sourceType === "console" ||
    preflight.sourceType === "copilot"
  ) {
    return createElement(HistorySelectionState, {
      kind: "unsupported",
      title: t("historyList.thread.unsupportedHistory.title"),
      description: t("historyList.thread.unsupportedHistory.description"),
      actionLabel: t("historyList.detail.backToList"),
      onAction: clearLegacyHistorySelection,
    })
  }
  return createElement(ThreadList, { workspace })
}

export const HistoryList = (props: HistoryListProps = {}) => {
  const config = useSysConfig()
  if (config.threadHistoryPageEnabled === true && props.workspace) {
    return createElement(ThreadHistoryPageOwner, { workspace: props.workspace })
  }
  return createElement(
    LegacyHistoryList as ComponentType<HistoryListProps>,
    props
  )
}

export const History = HistoryPage
